<template>
  <div>
    <div v-if="$can('manage-all')">
      <v-row>
        <v-col>
          <v-card>
            <v-card-title>
              Player Info
            </v-card-title>
            <v-card-text>
              <v-row
                no-gutters
                class="py-2"
              >
                <v-col cols="4">
                  Serial
                </v-col>
                <v-col>
                  {{ player.serial }}
                </v-col>
              </v-row>
              <v-row
                no-gutters
                class="py-2"
              >
                <v-col cols="4">
                  Client
                </v-col>
                <v-col>
                  <span v-if="player.client">{{ player.client.name }}</span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    n/a
                  </span>
                </v-col>
              </v-row>
              <v-row
                no-gutters
                class="py-2"
              >
                <v-col cols="4">
                  First Online
                </v-col>
                <v-col>
                  <span v-if="player.created_at">{{ $dayjs(player.created_at).format('DD.MM.YYYY. HH:mm:ss') }}</span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    n/a
                  </span>
                </v-col>
              </v-row>
              <v-row
                no-gutters
                class="py-2"
              >
                <v-col cols="4">
                  Firmware Version
                </v-col>
                <v-col>
                  <span v-if="player.firmware">{{ player.firmware.version }}</span>
                  <span
                    v-else
                    class="grey--text"
                  >
                    n/a
                  </span>
                </v-col>
              </v-row>
              <v-row
                no-gutters
                class="py-2"
              >
                <v-col cols="4">
                  Radio
                </v-col>
                <v-col>
                  <router-link
                    v-if="player.radio"
                    :to="{ name: 'radio', params: { id: player.radio.id }}"
                  >
                    {{ player.radio.title }}
                  </router-link>
                  <span
                    v-else
                    class="grey--text"
                  >
                    n/a
                  </span>
                </v-col>
              </v-row>
              <v-row
                no-gutters
                class="py-2"
              >
                <v-col cols="4">
                  Network configuration
                </v-col>
                <v-col cols="8">
                  <div>{{ player.is_static_network ? 'Static' : 'DHCP' }}</div>
                </v-col>
              </v-row>
              <v-card
                v-if="player.is_static_network"
                outlined
              >
                {{ player.static_network_configuration }}
              </v-card>
              <v-row
                no-gutters
                class="py-2"
              >
                <v-col cols="4">
                  Hostname
                </v-col>
                <v-col cols="8">
                  <div>{{ player.has_manual_hostname ? 'Manual: ' + player.hostname : 'Automatic' }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col>
          <v-card>
            <v-card-title class="no-gutters">
              <v-col>
                Diagnostics
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="primary"
                  @click="runDiagnostics()"
                >
                  Run Diagnostics
                </v-btn>
              </v-col>
            </v-card-title>
            <v-card-text
              v-if="diagnosing"
              class="pa-6 text-center"
            >
              <v-progress-circular
                :size="60"
                color="primary"
                indeterminate
              />
              <div class="mt-6 subtitle-1">
                Running player diagnosis...
              </div>
            </v-card-text>
            <v-card-text v-else-if="player.diagnostics">
              <div class="body-1">
                Network
              </div>
              <pre class="grey darken-3 white--text caption pa-3">{{ player.diagnostics.network }}</pre>

              <div class="body-1 mt-3">
                Disk Usage
              </div>
              <pre class="grey darken-3 white--text caption pa-3">{{ player.diagnostics.disk_usage }}</pre>

              <div class="body-1 mt-3">
                Temperature
              </div>
              <pre class="grey darken-3 white--text caption pa-3">{{ player.diagnostics.temperature }}</pre>

              <div class="body-1 mt-3">
                Uptime / Load
              </div>
              <pre class="grey darken-3 white--text caption pa-3">{{ player.diagnostics.uptime_and_load }}</pre>

              <div class="body-1 mt-3">
                Memory
              </div>
              <pre class="grey darken-3 white--text caption pa-3">{{ player.diagnostics.memory }}</pre>

              <div class="body-1 mt-3">
                Failed Units
              </div>
              <pre class="grey darken-3 white--text caption pa-3">{{ player.diagnostics.systemd_failed_units }}</pre>
            </v-card-text>
            <v-card-text v-else>
              <div class="subtitle-1 text-center pa-6 grey--text">
                No diagnostics yet. Please run diagnostics.
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <div
      v-else
      class="mt-4"
    >
      <v-card
        v-for="radio in radios"
        :key="radio.id"
        :elevation="radio.id === player.radio.id ? 20 : undefined"
        :color="radio.id === player.radio.id ? 'primary' : undefined"
        class="mb-2"
        :dark="radio.id === player.radio.id"
        @click="initSwitchRadio(radio)"
      >
        <v-card-title>
          <v-row no-gutters>
            <v-col>
              {{ radio.title }}
            </v-col>
            <v-col cols="auto">
              <v-btn
                v-if="radio.id === player.radio.id"
                disabled
                dark
              >
                Playing
              </v-btn>
              <v-btn
                v-else
                color="secondary"
                @click="initSwitchRadio(radio)"
              >
                <v-icon
                  left
                  small
                >
                  fa fa-play
                </v-icon>
                Play
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text>
          {{ radio.description }}
        </v-card-text>
      </v-card>
    </div>

    <player-assign-radio
      v-if="switchRadio.newRadio"
      :visible.sync="switchRadio.dialog"
      :radio="switchRadio.newRadio"
      :player="player"
      @success="$emit('update')"
    />
  </div>
</template>

<script>
import Players from '@/services/Players'
import Radios from '@/services/Radios'
import PlayerAssignRadio from '@/components/players/PlayerAssignRadio'

export default {
name: 'PlayerDetails',
  components: { PlayerAssignRadio },
  props: {
    player: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      diagnosing: false,
      radios: [],
      switchRadio: {
        newRadio: undefined,
        dialog: false,
        assigning: false
      }
    }
  },
  watch: {
    player() {
      this.diagnosing = false
    }
  },
  created() {
    this.fetchRadios()
  },
  methods: {
    async fetchRadios() {
      this.radios = await Radios.fetch()
    },
    runDiagnostics() {
      this.diagnosing = true

      Players.runDiagnostics([this.player.id])
    },
    initSwitchRadio(newRadio) {
      if (newRadio.id === this.player.radio_id) return

      this.switchRadio.newRadio = newRadio
      this.switchRadio.dialog = true
    }
  }
}
</script>
